/**
 * ChatbotService — HTTP client for the flood chatbot backend endpoint.
 * Mirrors the pattern established in FloodService.js.
 * @author: Anshu Sharma
 * @date: 2026-06-10
 */

import fetch from "auth/FetchInterceptor";
import {
  Application_Json,
  Authorization_Token,
  Chatbot_Ask,
} from "constants/ApiConstant";
import { format } from "react-string-format";
import { AUTH_TOKEN } from "redux/constants/Auth";

const ChatbotService = {};

/**
 * Send a message to the flood chatbot backend.
 * @param {string} message  The user's natural-language question
 * @param {Array<{role:'user'|'assistant', content:string}>} [history]
 *   Prior conversation turns (excluding the greeting and the current question).
 *   Passed through as-is; backend treats missing history as empty.
 * @param {File[]} [attachments]
 *   Optional array of File objects (images or PDFs) to send alongside the message.
 *   When non-empty, the request is sent as multipart/form-data — do NOT pass a
 *   Content-Type header; the browser sets the multipart boundary automatically.
 *   When empty (default), the existing JSON path is used unchanged.
 * @returns {Promise<{ answer: string, model?: string, usage?: object, stopReason?: string, toolCalls?: string[] }>}
 */
ChatbotService.ask = function (message, history, attachments) {
  const files = Array.isArray(attachments) ? attachments : [];

  if (files.length > 0) {
    // Multipart path — browser sets Content-Type + boundary automatically.
    // Do NOT include a "Content-Type" key; axios + the browser handle it.
    const formData = new FormData();
    formData.append("message", message);
    formData.append("history", JSON.stringify(history || []));
    files.forEach((file) => {
      // Backend reads ctx.request.files.files — field name must be "files"
      formData.append("files", file);
    });

    return fetch({
      url: Chatbot_Ask,
      method: "post",
      headers: {
        // Omit Content-Type — multipart/form-data boundary is set by the browser
        Authorization: format(
          Authorization_Token,
          localStorage.getItem(AUTH_TOKEN)
        ),
      },
      data: formData,
      skipPageLoading: true,
    });
  }

  // JSON path (no attachments) — unchanged from original
  return fetch({
    url: Chatbot_Ask,
    method: "post",
    headers: {
      "Content-Type": Application_Json,
      Authorization: format(
        Authorization_Token,
        localStorage.getItem(AUTH_TOKEN)
      ),
    },
    data: { message, history },
    skipPageLoading: true,
  });
};

export default ChatbotService;
